import _ from 'lodash';

// The controller and parent proxies survive connection resync. Pending UI state
// must survive the editor unmount too, without becoming readback or browser storage.
// Both override editors use this session so a newer save supersedes either page.
const editorSubmissions = new WeakMap();
export function getOverwriteEditorSubmissions(controller, parent) {
  if (!editorSubmissions.has(controller)) editorSubmissions.set(controller, new WeakMap());
  const zones = editorSubmissions.get(controller);
  if (!zones.has(parent)) zones.set(parent, { current: {} });
  return zones.get(parent);
}

// Matches GCSPI_MULTI_IO_OVERWRITE_CONFIGURED; no wire field was added.
export const IO_OVERRIDE_CONFIGURED = 0x80;
// Accept the wizard's type name or the GUI's normalized hardware category.
export const getNewOverwriteMode = (type) =>
  type === 'analog_out' || Number(type) === 3 ? 2 : 3;

export const isIoOverrideConfigured = (row) =>
  Number(row.overwr_select) > 0 || (Number(row.overwr_add_sett) & IO_OVERRIDE_CONFIGURED) !== 0;

// Remove only the retired browser membership cache. Never turn it into settings.
export function clearLegacyIoOverrideRowsStorage() {
  try {
    for (let i = localStorage.length - 1; i >= 0; --i) {
      const key = localStorage.key(i);
      if (key?.startsWith('io_override_rows:v1:')) localStorage.removeItem(key);
    }
  } catch {
    /* The controller remains authoritative when browser storage is unavailable. */
  }
}

const fields = ['select', 'val_ana', 'add_sett', 'timeout'];
const key = (row) => `${row.zone}:${row.function}:${row.func_index ?? 0}`;
const owns = (object, field) => Object.prototype.hasOwnProperty.call(object, field);

// The controller owns the countdown and Auto state. Apply only explicit user
// edits to its latest complete list; never merge arrays by their row positions.
export function buildFunctionOverwrites(latest, edited, drafts = {}, zone, indexConfig = {}) {
  const rows = new Map(
    (latest?.oios ?? []).filter(Boolean).map((row) => [key(row), _.cloneDeep(row)])
  );
  (edited?.oios ?? []).forEach((row, index) => {
    if (!row || String(row.zone) !== String(zone)) return;
    const identity = key(row);
    if (
      row.__deleted__ ||
      (owns(drafts, `overwr_io_${index}_function`) &&
        drafts[`overwr_io_${index}_function`] === undefined)
    ) {
      rows.delete(identity);
      return;
    }
    const changed = fields.filter((field) => owns(drafts, `overwr_io_${index}_${field}`));
    if (!changed.length) return;
    // Editing the value of an expired/deleted override does not re-enable it.
    const current = rows.get(identity) ?? { ...row, select: 0, timeout: '00:00:00' };
    changed.forEach((field) => {
      current[field] = drafts[`overwr_io_${index}_${field}`];
    });
    rows.set(identity, current);
  });

  Object.keys(drafts)
    .filter((name) => /^overwr_io_add_overwr_io_\d+$/.test(name))
    .sort((a, b) => Number(a.split('_').pop()) - Number(b.split('_').pop()))
    .forEach((name) => {
      const index = name.split('_').pop();
      const functionId = drafts[`overwr_io_func_add_overwr_io_${index}`];
      const functionIndex = drafts[`overwr_io_func_index_add_overwr_io_${index}`];
      if (functionId === undefined || (indexConfig[functionId] && functionIndex === undefined))
        return;
      const row = {
        zone: String(zone),
        function: functionId,
        func_index: indexConfig[functionId] ? functionIndex : 0,
        select: drafts[`overwr_io_select_add_overwr_io_${index}`] ??
          getNewOverwriteMode(drafts[`overwr_io_type_add_overwr_io_${index}`]),
        val_ana: drafts[`overwr_io_val_ana_add_overwr_io_${index}`] ?? 0,
        add_sett: 0,
        timeout: drafts[`overwr_io_timeout_add_overwr_io_${index}`] ?? '00:00:00'
      };
      rows.set(key(row), row);
    });
  const oios = Array.from(rows.values());
  return { functions_used_cnt: oios.length, oios };
}

// Preserve row indices while editing, since draft field names contain them.
export function syncFunctionOverwrites(current, latest, drafts = {}) {
  const incoming = new Map((latest?.oios ?? []).filter(Boolean).map((row) => [key(row), row]));
  const oios = (current?.oios ?? []).map((row, index) => {
    if (!row) return row;
    const live = incoming.get(key(row));
    incoming.delete(key(row));
    if (row.__deleted__) return row;
    const next = live ? { ...live } : { ...row, select: 0, timeout: '00:00:00' };
    fields.forEach((field) => {
      if (owns(drafts, `overwr_io_${index}_${field}`)) next[field] = row[field];
    });
    return next;
  });
  oios.push(...Array.from(incoming.values(), (row) => ({ ...row })));
  const next = { functions_used_cnt: oios.filter(Boolean).length, oios };
  return _.isEqual(current, next) ? current : next;
}

export function buildIoOverwrites(latest, edited, drafts = {}, includeAdditions = true) {
  const result = _.cloneDeep(latest);
  for (const [slave, ports] of Object.entries(result)) {
    for (const [port, row] of Object.entries(ports)) {
      const local = edited?.[slave]?.[port];
      if (!local || local._index !== row._index) continue;
      for (const field of [
        'overwr_select',
        'overwr_val_ana',
        'overwr_add_sett',
        'overwr_timeout'
      ]) {
        const name = `multi_out_${slave}_${port}_${field}`;
        if (owns(drafts, name)) row[field] = drafts[name];
      }
      if (drafts[`multi_out_${slave}_${port}_overwr_deleted`]) {
        row.overwr_add_sett = Number(row.overwr_add_sett) & ~IO_OVERRIDE_CONFIGURED;
      } else if (
        Number(row.overwr_select) > 0 ||
        owns(drafts, `multi_out_${slave}_${port}_overwr_select`)
      ) {
        row.overwr_add_sett = Number(row.overwr_add_sett) | IO_OVERRIDE_CONFIGURED;
      }
    }
  }
  for (const name of includeAdditions ? Object.keys(drafts) : []) {
    const match = name.match(/^multi_out_(\d+)_id_add_overwr_io_(\d+)$/);
    if (!match) continue;
    const [, slave, index] = match;
    const port = drafts[name];
    const row = result[slave]?.[port];
    if (!row) continue;
    row.overwr_select = drafts[`multi_out_${slave}_select_add_overwr_io_${index}`] ??
      getNewOverwriteMode(row.type_id);
    row.overwr_val_ana = drafts[`multi_out_${slave}_val_ana_add_overwr_io_${index}`] ?? 0;
    row.overwr_add_sett = Number(row.overwr_add_sett) | IO_OVERRIDE_CONFIGURED;
    row.overwr_timeout = drafts[`multi_out_${slave}_timeout_add_overwr_io_${index}`] ?? '00:00:00';
  }
  return result;
}

const ioFields = ['overwr_select', 'overwr_val_ana', 'overwr_add_sett', 'overwr_timeout'];
const timeoutSeconds = (value = '00:00:00') =>
  String(value)
    .split(':')
    .reduce((total, part) => total * 60 + Number(part), 0);
const normalized = (row, io) =>
  row &&
  Object.fromEntries(
    (io ? ioFields : fields).map((field, i) => [
      fields[i],
      String(row[field] ?? (fields[i] === 'timeout' ? '00:00:00' : 0))
    ])
  );
const assignment = (row) =>
  row && [row._index, row.func, row.func_index, row.zone, row.type_id].join(':');
function overwriteRows(settings) {
  const rows = {};
  (settings.overwr_io?.oios ?? []).filter(Boolean).forEach((row) => {
    rows[`f:${key(row)}`] = { row, io: false };
  });
  Object.entries(settings.multi_io ?? {}).forEach(([slave, ports]) => {
    Object.entries(ports).forEach(([port, row]) => {
      rows[`i:${slave}:${port}`] = { row, io: true, slave, port };
    });
  });
  return rows;
}

// Submitted changes outlive the draft. They are removed only by a matching
// changed readback, a physical remap, reset, or the submitted finite timeout.
export function recordOverwriteSubmissions(sent, live, pending = {}, now = Date.now()) {
  const desired = overwriteRows(sent);
  const saved = overwriteRows(live);
  const next = { ...pending };
  for (const identity of new Set([...Object.keys(desired), ...Object.keys(saved)])) {
    const entry = desired[identity] ?? saved[identity];
    const value = desired[identity]?.row;
    const baseline = saved[identity]?.row;
    if (
      _.isEqual(normalized(value, entry.io), normalized(baseline, entry.io)) &&
      !pending[identity]
    )
      continue;
    if (
      pending[identity] &&
      _.isEqual(normalized(value, entry.io), normalized(pending[identity].row, entry.io))
    )
      continue;
    const seconds = timeoutSeconds(value?.[entry.io ? 'overwr_timeout' : 'timeout']);
    next[identity] = {
      ...entry,
      row: _.cloneDeep(value),
      previous: normalized(baseline, entry.io),
      assignment: entry.io ? assignment(baseline) : null,
      deadline: seconds > 0 ? now + seconds * 1000 : null
    };
  }
  return next;
}

export function reconcileOverwriteSubmissions(pending, live, now = Date.now()) {
  const saved = overwriteRows(live);
  const next = {};
  for (const [identity, entry] of Object.entries(pending)) {
    const row = saved[identity]?.row;
    if (entry.io && assignment(row) !== entry.assignment) continue;
    const actual = normalized(row, entry.io);
    const desired = normalized(entry.row, entry.io);
    const changed = !_.isEqual(actual, entry.previous);
    const matching = !desired
      ? !actual
      : actual &&
        fields
          .filter((field) => field !== 'timeout')
          .every((field) => actual[field] === desired[field]) &&
        (desired.timeout === '00:00:00'
          ? actual.timeout === desired.timeout
          : timeoutSeconds(actual.timeout) > 0 &&
            timeoutSeconds(actual.timeout) <= timeoutSeconds(desired.timeout));
    if (changed && matching) continue;
    // An expiry can arrive before the first active readback. Do not keep an
    // optimistic finite override alive after its requested duration.
    if (entry.deadline !== null && now >= entry.deadline) continue;
    next[identity] = { ...entry, previous: actual };
  }
  return next;
}

export function applyOverwriteSubmissions(live, pending) {
  const result = _.cloneDeep(live);
  const functions = new Map(
    (result.overwr_io?.oios ?? []).filter(Boolean).map((row) => [key(row), row])
  );
  for (const [identity, entry] of Object.entries(pending)) {
    if (entry.io) {
      const row = result.multi_io?.[entry.slave]?.[entry.port];
      if (row && assignment(row) === entry.assignment) {
        ioFields.forEach((field) => {
          row[field] = entry.row[field];
        });
      }
    } else if (entry.row) functions.set(key(entry.row), _.cloneDeep(entry.row));
    else functions.delete(identity.slice(2));
  }
  result.overwr_io = { functions_used_cnt: functions.size, oios: [...functions.values()] };
  return result;
}
